'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import React, { useState, useEffect, useRef } from 'react'
import { useLenis } from '@/providers/LenisProvider'
import { registerForCamp, checkCampEnrollment } from '@/actions/registration'
import { getAlternativeCamps } from '@/actions/camps'
import { loginTeilnehmer } from '@/actions/teilnehmerAuth'
import { useAuth } from '@/hooks/useAuth'
import { getRegistrationFormTranslations, type RegistrationFormTranslations } from './translations'
import type { Camp } from '@/payload-types'
import { pushCampRegistrationFormSubmit } from '@/utilities/gtmFormSubmit'
import { isFamilyCamp } from '@/utilities/campCategoryUtils'
import { Button } from '../ui/button'

interface RegistrationFormProps {
  campTitle: string
  campId: string // Add campId prop
  isPaidCamp: boolean
  price?: number
  isEnglishCamp?: boolean // Add this prop for language selection
  camp?: Camp // Add full camp data
}

type FormStep =
  | 'initial'
  | 'login'
  | 'register'
  | 'personal'
  | 'organizational'
  | 'legal'
  | 'success'

// Add interface for alternative camps
interface AlternativeCamp {
  id: string
  title: string
  date: string
  slug: string // Add slug
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  campTitle,
  campId,
  isPaidCamp,
  price,
  isEnglishCamp = false, // Default to German
  camp,
}) => {
  // Get translations based on language
  const t: RegistrationFormTranslations = getRegistrationFormTranslations(isEnglishCamp)

  // Check if this is a family camp (shows children fields)
  const isFamilyCampFlag = isFamilyCamp(camp)
  const showEmergencyEquipment = camp?.askEmergencyEquipmentInRegistration === true
  const showMultiPitchInterest = camp?.askMultiPitchInterestInRegistration === true
  const showLiftTicketOption = camp?.liftTicketAvailable === true

  const { lenis } = useLenis()
  const [currentStep, setCurrentStep] = useState<FormStep>('initial')
  const [isOpen, setIsOpen] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const successRef = useRef<HTMLDivElement>(null)

  // Add new state for user mode and loading
  const [userMode, setUserMode] = useState<'new' | 'existing'>('new')
  const [isLoadingUserData, setIsLoadingUserData] = useState(false)
  const [loginError, setLoginError] = useState<string>('')

  // Add state for alternative camps
  const [alternativeCamps, setAlternativeCamps] = useState<AlternativeCamp[]>([])
  const [loadingAlternatives, setLoadingAlternatives] = useState(false)

  // Add state for enrollment status
  const [enrollmentStatus, setEnrollmentStatus] = useState<{
    isEnrolled: boolean
    registrationStatus: string | null
    isLoading: boolean
  }>({
    isEnrolled: false,
    registrationStatus: null,
    isLoading: true,
  })

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    emailReg: '',
    phone: '',
    street: '',
    plz: '',
    city: '',
    country: '',
    birthday: '',
    passwordReg: '',
    passwordRepeat: '',
    alternativeCamp: '',
    needsEquipment: false,
    interestMultiPitch: false,
    needsLiftTicket: false,
    photoConsent: false,
    liabilityConsent: false,
    newsletter: false,
    saveLogin: false,
    // Family camp children fields
    children: [] as Array<{
      name: string
      birthday: string
      needsEquipment: boolean
      needsLiftTicket: boolean
    }>,
  })

  const { user, isLoggedIn, loading, refreshAuth } = useAuth()

  // Helper functions for managing children
  const addChild = () => {
    setFormData((prev) => ({
      ...prev,
      children: [
        ...prev.children,
        {
          name: '',
          birthday: '',
          needsEquipment: false,
          needsLiftTicket: false,
        },
      ],
    }))
  }

  const removeChild = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index),
    }))
  }

  const updateChild = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.map((child, i) =>
        i === index ? { ...child, [field]: value } : child,
      ),
    }))
  }

  // Add this state after the existing state declarations (around line 90)
  const [validationError, setValidationError] = useState<{
    field?: string
    message: string
  } | null>(null)

  // Update the validation to track which specific fields have errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Add this state for mobile detection
  const [isMobile, setIsMobile] = useState(false)
  const [isClient, setIsClient] = useState(false)

  // Add this useEffect to handle mobile detection after hydration
  useEffect(() => {
    setIsClient(true)
    setIsMobile(window.innerWidth < 768)

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // The form is much taller than the confirmation that replaces it, so after
  // submitting the page shrinks and the confirmation ends up off-screen. Bring it
  // into view once it is rendered, otherwise people do not notice it worked.
  useEffect(() => {
    if (!isSent || !successRef.current) return
    const target = successRef.current
    if (lenis) {
      lenis.scrollTo(target, { offset: -120, duration: 1.2 })
    } else {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    target.focus({ preventScroll: true })
  }, [isSent, lenis])

  // Fetch alternative camps on component mount
  useEffect(() => {
    const fetchAlternativeCamps = async () => {
      setLoadingAlternatives(true)
      try {
        const result = await getAlternativeCamps(campId)
        if (result.success) {
          setAlternativeCamps(result.camps as AlternativeCamp[])
        }
      } catch (error) {
        console.error('Failed to fetch alternative camps:', error)
      } finally {
        setLoadingAlternatives(false)
      }
    }

    if (campId) {
      fetchAlternativeCamps()
    }
  }, [campId])

  // Add this useEffect after the existing one for alternative camps
  useEffect(() => {
    // Pre-fill form data if user is already logged in
    if (isLoggedIn && user && !loading) {
      setFormData(
        (prev) =>
          ({
            ...prev,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            emailReg: user.email || '',
            phone: user.phone || '',
            street: user.street || '',
            plz: user.plz || '',
            city: user.city || '',
            country: user.country || '',
            birthday: user.birthday ? new Date(user.birthday).toISOString().split('T')[0] : '',
            newsletter: user.newsletter || false,
          }) as typeof prev,
      )

      setUserMode('existing')
    }
  }, [isLoggedIn, user, loading])

  // Check enrollment status when user logs in or component mounts
  useEffect(() => {
    const checkEnrollment = async () => {
      if (isLoggedIn && user && campId) {
        setEnrollmentStatus((prev) => ({ ...prev, isLoading: true }))
        try {
          const result = await checkCampEnrollment(campId)
          setEnrollmentStatus({
            isEnrolled: result.isEnrolled,
            registrationStatus: result.registrationStatus || null,
            isLoading: false,
          })
        } catch (error) {
          console.error('Error checking enrollment:', error)
          setEnrollmentStatus((prev) => ({ ...prev, isLoading: false }))
        }
      } else if (!isLoggedIn) {
        setEnrollmentStatus({
          isEnrolled: false,
          registrationStatus: null,
          isLoading: false,
        })
      }
    }

    checkEnrollment()
  }, [isLoggedIn, user, campId])

  const scrollToTop = () => {
    if (lenis) {
      const targetElement = document.getElementById('anmeldung')
      if (targetElement) {
        const elementPosition = targetElement.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - 50 // 80px offset from top

        lenis.scrollTo(offsetPosition, {
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        })
      }
    }
  }

  const handleStepChange = (step: FormStep) => {
    setCurrentStep(step)
    scrollToTop()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement
      setFormData((prev) => ({
        ...prev,
        [name]: target.checked,
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    // Consent is the one gate both layouts have to share. The desktop form can
    // lean on the browser, but the mobile wizard is not a <form>, so `required`
    // never runs there. Checking here covers both and saves a round trip.
    if (!formData.photoConsent || !formData.liabilityConsent) {
      setFieldErrors((prev) => ({
        ...prev,
        ...(formData.photoConsent ? {} : { photoConsent: t.validation.fieldRequired }),
        ...(formData.liabilityConsent ? {} : { liabilityConsent: t.validation.fieldRequired }),
      }))
      setValidationError({ message: t.validation.consentRequired })
      return
    }

    // Create FormData object
    const formDataObj = new FormData()

    // Add all form fields to FormData
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'children') {
        // Handle children array specially
        if (Array.isArray(value)) {
          formDataObj.append('children', JSON.stringify(value))
        }
      } else if (typeof value === 'boolean') {
        if (value) formDataObj.append(key, 'on')
      } else {
        formDataObj.append(key, value.toString())
      }
    })

    // Add campId
    formDataObj.append('campId', campId)

    try {
      const result = await registerForCamp(formDataObj)

      if (result.success) {
        const campform =
          typeof camp?.category === 'object' && camp?.category?.name
            ? camp.category.name
            : undefined
        pushCampRegistrationFormSubmit({
          formId: 'camp-anmeldung',
          formTarget: typeof window !== 'undefined' ? window.location.pathname : undefined,
          campform,
          newsletterBestellt: formData.newsletter,
          campId,
          campTitle,
        })
        setIsSent(true)
      } else {
        console.error('Registration failed:', result.error)
        setValidationError({ message: result.error || t.validation.submitFailed })
      }
    } catch (error) {
      console.error('Submission error:', error)
      setValidationError({ message: t.validation.submitFailed })
    }
  }

  // Add login handler
  const handleLogin = async (e: React.FormEvent) => {
    console.log('handleLogin called!') // Add this line
    e.preventDefault()
    setIsLoadingUserData(true)
    setLoginError('')

    const loginFormData = new FormData()
    loginFormData.append('email', formData.email)
    loginFormData.append('password', formData.password)

    try {
      const result = await loginTeilnehmer(loginFormData)

      if (result.success && result.userData) {
        // Pre-fill form with user data
        setFormData(
          (prev) =>
            ({
              ...prev,
              firstName: result.userData!.firstName,
              lastName: result.userData!.lastName,
              emailReg: result.userData!.email, // Note: using email now
              phone: result.userData!.phone,
              street: result.userData!.street,
              plz: result.userData!.plz,
              city: result.userData!.city,
              country: result.userData!.country,
              birthday: result.userData?.birthday
                ? new Date(result.userData.birthday).toISOString().split('T')[0]
                : '',
              newsletter: result.userData?.newsletter || false,
            }) as typeof prev,
        )

        setUserMode('existing')

        // Refresh auth state to show login status immediately
        await refreshAuth()

        handleStepChange('personal')
      } else {
        setLoginError(result.error || t.errors.loginFailed)
      }
    } catch (error) {
      setLoginError(t.errors.submissionError)
    } finally {
      setIsLoadingUserData(false)
    }
  }

  // Add this simple validation function after the existing functions
  const validateCurrentStep = () => {
    console.log('Validating step:', currentStep)

    const stepRequiredFields: Record<string, string[]> = {
      personal: [
        'firstName',
        'lastName',
        'emailReg',
        'phone',
        'street',
        'plz',
        'city',
        'country',
        'birthday',
        // Only require password fields if user is not logged in
        ...(isLoggedIn ? [] : ['passwordReg', 'passwordRepeat']),
      ],
      organizational: [],
      legal: ['photoConsent', 'liabilityConsent'],
    }

    const requiredFields = stepRequiredFields[currentStep] || []
    const newFieldErrors: Record<string, string> = {}

    for (const fieldName of requiredFields) {
      const fieldValue = formData[fieldName as keyof typeof formData]
      console.log(`Field ${fieldName}:`, fieldValue, 'Type:', typeof fieldValue)

      if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
        console.log(`Validation failed for ${fieldName}`)
        newFieldErrors[fieldName] = t.validation.fieldRequired
      }
    }

    console.log('Field errors:', newFieldErrors)
    setFieldErrors(newFieldErrors)
    return Object.keys(newFieldErrors).length === 0
  }

  /** Spells out why a field is marked, next to the red border. */
  const FieldError = ({ name }: { name: string }) =>
    fieldErrors[name] ? (
      <p className="mt-1 text-sm text-red-600" role="alert">
        {fieldErrors[name]}
      </p>
    ) : null

  /** Summary shown next to the button the visitor just pressed. */
  const SubmitError = () =>
    validationError ? (
      <p className="mt-3 text-sm text-red-600" role="alert">
        {validationError.message}
      </p>
    ) : null

  // Update only the nextStep function to use validation
  const nextStep = () => {
    console.log('nextStep called!')
    switch (currentStep) {
      case 'initial':
        handleStepChange('login')
        break
      case 'login':
        // This will be handled by handleLogin instead
        break
      case 'register':
        setUserMode('new')
        handleStepChange('personal')
        break
      case 'personal':
        if (validateCurrentStep()) {
          handleStepChange('organizational')
        }
        break
      case 'organizational':
        if (validateCurrentStep()) {
          handleStepChange('legal')
        }
        break
      case 'legal':
        if (validateCurrentStep()) {
          handleStepChange('success')
        }
        break
    }
  }

  const prevStep = () => {
    switch (currentStep) {
      case 'login':
        handleStepChange('initial')
        break
      case 'register':
        handleStepChange('initial')
        break
      case 'personal':
        // On mobile, go back to initial step instead of login
        handleStepChange('initial')
        break
      case 'organizational':
        handleStepChange('personal')
        break
      case 'legal':
        handleStepChange('organizational')
        break
    }
  }

  // Add a helper function to get the selected camp details
  const getSelectedAlternativeCamp = () => {
    if (!formData.alternativeCamp) return null
    return alternativeCamps.find((camp) => camp.id === formData.alternativeCamp)
  }

  if (isSent) {
    return (
      <div
        id="anmeldung"
        ref={successRef}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        className="mt-16 bg-custom-blue p-6 py-8 outline-none"
      >
        <div className="mx-auto flex w-full max-w-7xl flex-row items-start gap-4">
          <div className="relative h-12 w-12 shrink-0">
            <Image
              src="/checkanmeldung.png"
              alt=""
              width={300}
              height={300}
              className="h-full w-full object-contain"
            />
          </div>
          <div className="space-y-2">
            <p className="text-2xl font-bold">{t.status.success.headline}</p>
            <p>{t.status.success.message}</p>
          </div>
        </div>
      </div>
    )
  }

  // Show enrollment info message when user is already enrolled
  if (isLoggedIn && !enrollmentStatus.isLoading && enrollmentStatus.isEnrolled) {
    return (
      <div className="mt-16 bg-custom-blue p-6 py-8">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center">
          <div className="flex flex-row items-start gap-4">
            <div className="relative h-10 w-10">
              <Image
                src="/checkanmeldung.png"
                alt={t.status.alreadyEnrolled.title}
                width={300}
                height={300}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="">
              <p className="text-left text-gray-600">
                {t.status.alreadyEnrolled.message
                  .replace('{firstName}', user?.firstName || '')
                  .replace('{campTitle}', campTitle)}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Mobile Layout with animation
  if (isClient && isMobile) {
    return (
      <div className="" id="anmeldung">
        <AnimatePresence mode="wait">
          {!isOpen ? (
            <motion.div
              key="button"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-start justify-start p-6"
            >
              <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-start py-8">
                <button
                  onClick={() => setIsOpen(true)}
                  className="flex flex-row items-center justify-center gap-2 rounded-lg border border-black bg-accent px-8 py-4 text-xl font-bold text-black transition-colors hover:bg-accent/80"
                >
                  {t.actions.submitApplication} <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={{
                height: { duration: 0.6, ease: [0.04, 0.62, 0.23, 0.98] },
                opacity: { duration: 0.4, delay: 0.2 },
              }}
              style={{ overflow: 'hidden' }}
            >
              {/* Progress Indicator */}
              <div className="flex aspect-video w-full items-center justify-center p-4">
                <Image
                  src="/anmeldung.png"
                  alt="Anmeldung Image"
                  width={500}
                  height={500}
                  className="w-full object-cover"
                />
              </div>
              <div className={`${currentStep !== 'initial' ? 'bg-custom-blue' : ''} p-4`}>
                {currentStep !== 'initial' && (
                  <div>
                    <h1 className="text-xl !font-normal text-gray-900">
                      {t.steps.campRegistration}
                    </h1>
                    <div className="mb-6 flex w-full justify-center">
                      <div className="flex w-full space-x-2">
                        <div
                          className={`h-1 w-full ${currentStep === 'login' || currentStep === 'personal' || currentStep === 'organizational' ? 'bg-accent' : 'bg-gray-300'}`}
                        />
                        <div
                          className={`h-1 w-full ${currentStep === 'organizational' || currentStep === 'legal' ? 'bg-accent' : 'bg-gray-300'}`}
                        />
                        <div
                          className={`h-1 w-full ${currentStep === 'legal' ? 'bg-accent' : 'bg-gray-300'}`}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step Content */}
                {currentStep === 'initial' && (
                  <div className="space-y-4">
                    <button
                      onClick={() => handleStepChange('login')}
                      className="w-full rounded-lg bg-accent py-4 font-normal text-black"
                    >
                      {t.actions.login}
                    </button>
                    <button
                      onClick={() => {
                        setUserMode('new')
                        handleStepChange('personal')
                      }}
                      className="w-full rounded-lg bg-accent py-4 font-semibold text-black"
                    >
                      {t.actions.register}
                    </button>
                  </div>
                )}

                {currentStep === 'login' && (
                  <div className="space-y-4">
                    <h2 className="text-lg !font-normal text-gray-900">{t.steps.login}</h2>
                    <div className="space-y-4">
                      <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.email}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                          placeholder={t.form.email}
                        />
                      </div>
                      <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.password}
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                          placeholder={t.form.password}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="saveLogin"
                            checked={formData.saveLogin}
                            onChange={handleInputChange}
                            className="h-4 w-4"
                          />
                          <span className="text-sm text-gray-700">{t.form.saveLogin}</span>
                        </label>
                        <button className="text-sm text-blue-600">{t.form.forgotPassword}</button>
                      </div>
                      <button
                        onClick={handleLogin}
                        disabled={isLoadingUserData}
                        className="flex w-full items-center justify-center rounded-lg bg-accent py-4 font-semibold text-black disabled:opacity-50"
                      >
                        {isLoadingUserData ? (
                          t.actions.loginLoading
                        ) : (
                          <>
                            {t.actions.loginButton}
                            <ChevronRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </button>
                      {loginError && <p className="mt-2 text-sm text-red-600">{loginError}</p>}
                      <div className="flex flex-col gap-4 text-center text-sm text-gray-600">
                        <p>{t.status.noAccount}</p>
                        <button
                          onClick={() => handleStepChange('personal')}
                          className="text-blue-600"
                        >
                          {t.status.registerHere}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Auth Status Display */}
                {!loading && isLoggedIn && (
                  <div className="mb-6 rounded-lg bg-gray-100 p-4">
                    <div className="text-green-600">
                      ✓ {t.status.loggedIn} {user?.firstName} {user?.lastName} ({user?.email})
                    </div>
                  </div>
                )}

                {currentStep === 'register' && (
                  <div className="space-y-4">
                    <h2 className="text-lg !font-normal text-gray-900">{t.steps.register}</h2>
                    <div className="space-y-4">
                      <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.email}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                          placeholder={t.form.email}
                        />
                      </div>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border-2 border-gray-300 bg-white p-4"
                        placeholder={t.form.password}
                      />
                      <div className="flex items-center justify-between">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="saveLogin"
                            checked={formData.saveLogin}
                            onChange={handleInputChange}
                            className="h-4 w-4"
                          />
                          <span className="text-sm text-gray-700">{t.form.saveLogin}</span>
                        </label>
                        <button className="text-sm text-blue-600">{t.form.forgotPassword}</button>
                      </div>
                      <button
                        onClick={nextStep}
                        className="flex w-full items-center justify-center rounded-lg bg-accent py-4 font-semibold text-black"
                      >
                        {t.actions.registerButton}
                      </button>
                      <div className="flex flex-col gap-4 text-center text-sm text-gray-600">
                        <p>{t.status.hasAccount}</p>
                        <button onClick={() => handleStepChange('login')} className="text-blue-600">
                          {t.status.registerHere}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 'personal' && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900">{t.steps.personal}</h2>

                    <div className="space-y-4">
                      <div
                        className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.firstName ? 'border-red-500' : ''}`}
                      >
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.firstName}
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent`}
                          placeholder={`${t.form.firstName}${t.form.required}`}
                          required
                        />
                        <FieldError name="firstName" />
                      </div>
                      <div
                        className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.lastName ? 'border-red-500' : ''}`}
                      >
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.lastName}
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                            fieldErrors.lastName ? 'border-red-500' : ''
                          }`}
                          placeholder={`${t.form.lastName}${t.form.required}`}
                          required
                        />
                        <FieldError name="lastName" />
                      </div>
                      <div
                        className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.emailReg ? 'border-red-500' : ''}`}
                      >
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.email}
                        </label>
                        <input
                          type="email"
                          name="emailReg"
                          value={formData.emailReg}
                          onChange={handleInputChange}
                          className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                            fieldErrors.emailReg ? 'border-red-500' : ''
                          }`}
                          placeholder={`${t.form.email}${t.form.required}`}
                          required
                        />
                        <FieldError name="emailReg" />
                      </div>
                      <div
                        className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.phone ? 'border-red-500' : ''}`}
                      >
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.phone}
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                            fieldErrors.phone ? 'border-red-500' : ''
                          }`}
                          placeholder={`${t.form.phone}${t.form.required}`}
                          required
                        />
                        <FieldError name="phone" />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div
                          className={`col-span-2 flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.street ? 'border-red-500' : ''}`}
                        >
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            {t.form.street}
                          </label>
                          <input
                            type="text"
                            name="street"
                            value={formData.street}
                            onChange={handleInputChange}
                            className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                              fieldErrors.street ? 'border-red-500' : ''
                            }`}
                            placeholder={`${t.form.street}${t.form.required}`}
                            required
                          />
                          <FieldError name="street" />
                        </div>
                        <div
                          className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.plz ? 'border-red-500' : ''}`}
                        >
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            {t.form.plz}
                          </label>
                          <input
                            type="text"
                            name="plz"
                            value={formData.plz}
                            onChange={handleInputChange}
                            className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                              fieldErrors.plz ? 'border-red-500' : ''
                            }`}
                            placeholder={`${t.form.plz}${t.form.required}`}
                            required
                          />
                          <FieldError name="plz" />
                        </div>
                      </div>
                      <div
                        className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.city ? 'border-red-500' : ''}`}
                      >
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.city}
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                            fieldErrors.city ? 'border-red-500' : ''
                          }`}
                          placeholder={`${t.form.city}${t.form.required}`}
                          required
                        />
                        <FieldError name="city" />
                      </div>
                      <div
                        className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.country ? 'border-red-500' : ''}`}
                      >
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.form.country}
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                            fieldErrors.country ? 'border-red-500' : ''
                          }`}
                          placeholder={`${t.form.country}${t.form.required}`}
                          required
                        />
                        <FieldError name="country" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div
                          className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.birthday ? 'border-red-500' : ''}`}
                        >
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            {t.form.birthday}
                          </label>
                          <input
                            type="date"
                            name="birthday"
                            value={formData.birthday}
                            onChange={handleInputChange}
                            className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                              fieldErrors.birthday ? 'border-red-500' : ''
                            }`}
                            placeholder="TT.MM.JJJJ"
                            required
                          />
                          <FieldError name="birthday" />
                        </div>
                      </div>
                      {!isLoggedIn && (
                        <div className="flex flex-col gap-4 pt-10">
                          <div className="relative">
                            <div
                              className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.passwordReg ? 'border-red-500' : ''}`}
                            >
                              <label className="mb-2 block text-sm font-medium text-gray-700">
                                {t.form.password}
                              </label>
                              <input
                                type="password"
                                name="passwordReg"
                                value={formData.passwordReg}
                                onChange={handleInputChange}
                                className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                                  fieldErrors.passwordReg ? 'border-red-500' : ''
                                }`}
                                placeholder={t.form.password}
                                required
                              />
                              <FieldError name="passwordReg" />
                            </div>
                            <button className="absolute right-4 top-1/2 -translate-y-1/2">
                              <svg
                                className="h-5 w-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                          </div>
                          <div className="relative">
                            <div
                              className={`flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 ${fieldErrors.passwordRepeat ? 'border-red-500' : ''}`}
                            >
                              <label className="mb-2 block text-sm font-medium text-gray-700">
                                {t.form.passwordRepeat}
                              </label>
                              <input
                                type="password"
                                name="passwordRepeat"
                                value={formData.passwordRepeat}
                                onChange={handleInputChange}
                                className={`w-full focus:border-transparent focus:ring-2 focus:ring-accent ${
                                  fieldErrors.passwordRepeat ? 'border-red-500' : ''
                                }`}
                                placeholder={t.form.passwordRepeat}
                                required
                              />
                              <FieldError name="passwordRepeat" />
                            </div>
                            <button className="absolute right-4 top-1/2 -translate-y-1/2">
                              <svg
                                className="h-5 w-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Children Section for Family Camps */}
                      {isFamilyCampFlag && (
                        <div className="space-y-4 pt-6">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {t.sections.children.title}
                          </h3>
                          <p className="text-sm text-gray-600">{t.sections.children.description}</p>

                          {formData.children.map((child, index) => (
                            <div key={index} className="space-y-4 rounded-xl">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-gray-900">
                                  {t.sections.children.child} {index + 1}
                                </h4>
                                {formData.children.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeChild(index)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    {t.sections.children.remove}
                                  </button>
                                )}
                              </div>

                              <div className="space-y-4">
                                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                                  <label className="mb-2 block text-sm font-medium text-gray-700">
                                    {t.sections.children.name}
                                  </label>
                                  <input
                                    type="text"
                                    value={child.name}
                                    onChange={(e) => updateChild(index, 'name', e.target.value)}
                                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                                    placeholder={t.sections.children.name}
                                  />
                                </div>

                                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                                  <label className="mb-2 block text-sm font-medium text-gray-700">
                                    {t.sections.children.birthday}
                                  </label>
                                  <input
                                    type="date"
                                    value={child.birthday}
                                    onChange={(e) => updateChild(index, 'birthday', e.target.value)}
                                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                                  />
                                </div>

                                <div className="space-y-3">
                                  {showEmergencyEquipment && (
                                    <label className="flex items-start space-x-3">
                                      <input
                                        type="checkbox"
                                        checked={child.needsEquipment}
                                        onChange={(e) =>
                                          updateChild(index, 'needsEquipment', e.target.checked)
                                        }
                                        className="relative mt-1 h-4 w-4"
                                      />
                                      <span className="text-sm text-gray-700">
                                        {t.sections.children.needsEquipment}
                                      </span>
                                    </label>
                                  )}

                                  {showLiftTicketOption && (
                                    <label className="flex items-start space-x-3">
                                      <input
                                        type="checkbox"
                                        checked={child.needsLiftTicket}
                                        onChange={(e) =>
                                          updateChild(index, 'needsLiftTicket', e.target.checked)
                                        }
                                        className="relative mt-1 h-4 w-4"
                                      />
                                      <span className="text-sm text-gray-700">
                                        {t.sections.children.needsLiftTicket}
                                      </span>
                                    </label>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={addChild}
                            className="flex cursor-pointer items-center gap-2 text-lg font-bold uppercase text-black"
                          >
                            + {t.sections.children.child} hinzufügen
                          </button>
                        </div>
                      )}

                      <div className="flex justify-between pt-4 text-sm">
                        <button
                          onClick={prevStep}
                          className="flex items-center gap-2 py-3 text-black"
                        >
                          <ChevronLeft /> {t.actions.back}
                        </button>
                        <button
                          onClick={nextStep}
                          className="flex items-center gap-2 py-3 text-black"
                        >
                          {t.actions.toOrganizational} <ChevronRight />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 'organizational' && (
                  <div className="space-y-4">
                    <h2 className="text-lg !font-normal text-gray-900">{t.steps.organizational}</h2>
                    <div className="space-y-4">
                      <select
                        name="alternativeCamp"
                        value={formData.alternativeCamp}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border-2 border-gray-300 bg-white p-2"
                        disabled={loadingAlternatives}
                      >
                        <option value="">
                          {loadingAlternatives
                            ? t.sections.alternativeDate.loadingAlternatives
                            : t.sections.alternativeDate.selectPlaceholder}
                        </option>
                        {alternativeCamps.map((camp) => (
                          <option key={camp.id} value={camp.id}>
                            {camp.title} - {new Date(camp.date).toLocaleDateString('de-DE')}
                          </option>
                        ))}
                        {!loadingAlternatives && alternativeCamps.length === 0 && (
                          <option value="" disabled>
                            {t.sections.alternativeDate.noAlternatives}
                          </option>
                        )}
                      </select>

                      {formData.alternativeCamp && (
                        <a
                          href={`/camps/${getSelectedAlternativeCamp()?.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 block text-sm underline"
                        >
                          {t.sections.alternativeDate.viewDetails}
                        </a>
                      )}

                      {showEmergencyEquipment && (
                        <label className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            name="needsEquipment"
                            checked={formData.needsEquipment}
                            onChange={handleInputChange}
                            className="relative mt-1 h-4 w-4"
                          />
                          <span className="text-lg text-gray-700">
                            {t.sections.equipment.emergencyEquipment}
                          </span>
                        </label>
                      )}

                      {showLiftTicketOption && (
                        <label className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            name="needsLiftTicket"
                            checked={formData.needsLiftTicket}
                            onChange={handleInputChange}
                            className="relative mt-1 h-4 w-4"
                          />
                          <span className="text-lg text-gray-700">
                            {t.sections.equipment.liftTicket}
                          </span>
                        </label>
                      )}

                      {showMultiPitchInterest && (
                        <label className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            name="interestMultiPitch"
                            checked={formData.interestMultiPitch}
                            onChange={handleInputChange}
                            className="relative mt-1 h-4 w-4"
                          />
                          <span className="text-lg text-gray-700">
                            {t.sections.equipment.multiPitchInterest}
                          </span>
                        </label>
                      )}

                      <div className="flex justify-between pt-4 text-sm">
                        <button
                          onClick={prevStep}
                          className="flex items-center gap-2 rounded-lg py-3 text-black"
                        >
                          <ChevronLeft /> {t.actions.back}
                        </button>
                        <button
                          onClick={nextStep}
                          className="flex items-center gap-2 py-3 text-black"
                        >
                          {t.actions.toLegal} <ChevronRight />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 'legal' && (
                  <div className="space-y-4">
                    <h2 className="text-lg !font-normal text-gray-900">{t.steps.legal}</h2>
                    <p className="text-gray-700">{t.sections.consent.title}</p>
                    <div className="space-y-4">
                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          name="photoConsent"
                          checked={formData.photoConsent}
                          onChange={handleInputChange}
                          className="relative mt-1 h-4 w-4"
                        />
                        <span className="text-lg text-gray-700">
                          <span
                            dangerouslySetInnerHTML={{ __html: t.sections.consent.photoConsent }}
                          />
                        </span>
                      </label>
                      <FieldError name="photoConsent" />

                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          name="liabilityConsent"
                          checked={formData.liabilityConsent}
                          onChange={handleInputChange}
                          className="relative mt-1 h-4 w-4"
                        />
                        <span
                          className="text-lg text-gray-700"
                          dangerouslySetInnerHTML={{ __html: t.sections.consent.liabilityConsent }}
                        />
                      </label>
                      <FieldError name="liabilityConsent" />

                      {/* Additional information text */}
                      <div className="ml-7 mt-2 text-sm text-gray-600">
                        <p>{t.sections.consent.additionalInfo}</p>
                        <p className="mt-1">{t.sections.consent.liabilityConfirmation}</p>
                      </div>

                      <h2 className="text-lg !font-normal text-gray-900">
                        {t.sections.newsletter.title}
                      </h2>

                      <p className="text-gray-700">{t.sections.newsletter.description}</p>

                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          name="newsletter"
                          checked={formData.newsletter}
                          onChange={handleInputChange}
                          className="relative mt-1 h-4 w-4"
                        />
                        <span className="text-lg text-gray-700">
                          {t.sections.newsletter.consent}
                        </span>
                      </label>

                      <div className="flex justify-between pt-4 text-sm">
                        <button
                          onClick={prevStep}
                          className="flex items-center gap-2 rounded-lg py-3 text-black"
                        >
                          <ChevronLeft /> {t.actions.back}
                        </button>
                        <button
                          onClick={handleSubmit}
                          className="rounded-lg bg-accent px-6 py-3 text-black"
                        >
                          {t.actions.apply}
                        </button>
                      </div>
                      <SubmitError />
                    </div>
                  </div>
                )}

                {currentStep === 'success' && (
                  <div className="flex flex-row items-start gap-4">
                    <div className="relative h-10 w-10">
                      <Image
                        src="/checkanmeldung.png"
                        alt={t.status.success.title}
                        width={300}
                        height={300}
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <div className="">
                      <p className="text-left text-gray-600">{t.status.success.message}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  // Desktop Layout with animation
  if (!isClient) {
    return (
      <div className="container px-4 py-6" id="anmeldung">
        <div className="mx-auto flex w-full flex-col items-start justify-start py-8">
          <button
            className="flex flex-row items-center justify-center gap-2 rounded-lg border border-black bg-accent px-8 py-4 text-xl font-bold text-black transition-colors hover:bg-accent/80"
            onClick={() => setIsOpen(true)}
          >
            {t.actions.submitApplication} <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>
    )
  }

  if (!isOpen) {
    return (
      <div className="container px-4 py-6" id="anmeldung">
        <div className="mx-auto flex w-full flex-col items-start justify-start py-8">
          <button
            onClick={() => setIsOpen(true)}
            className="flex flex-row items-center justify-center gap-2 rounded-lg border border-black bg-accent px-8 py-4 text-xl font-bold text-black transition-colors hover:bg-accent/80"
          >
            {t.actions.submitApplication} <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>
    )
  }

  // Desktop form with animation
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      transition={{
        height: { duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] },
        opacity: { duration: 0.5, delay: 0.3 },
      }}
      style={{ overflow: 'hidden' }}
      className="min-h-screen bg-custom-blue p-6 py-32"
      id="anmeldung"
    >
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <p className="mb-2 !text-gray-600 !font-clash !text-2xl">{t.sections.welcome.subtitle}</p>
          <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">{campTitle}</h1>
        </div>

        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-800">{t.sections.welcome.title}</h2>
          <p className="mb-4 text-gray-600">{t.sections.welcome.description}</p>
          <ul className="list-inside list-disc space-y-1 text-gray-600">
            {t.sections.welcome.processSteps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Login Section */}
          {!isLoggedIn && (
            <div className="rounded-lg p-6">
              <h3 className="mb-6 text-2xl font-semibold text-gray-800">{t.steps.login}</h3>
              <div className="grid items-end gap-4 md:grid-cols-4">
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t.form.email}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.email}
                  />
                </div>
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t.form.password}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.password}
                  />
                </div>
                <div className="flex w-full flex-col items-start justify-start gap-0 p-4">
                  <button
                    type="button"
                    onClick={handleLogin}
                    disabled={isLoadingUserData}
                    className="flex flex-row items-center justify-center gap-2 rounded-xl px-6 py-3 text-2xl font-semibold text-black transition-colors hover:scale-105 disabled:opacity-50"
                  >
                    {isLoadingUserData ? (
                      t.actions.loginLoading
                    ) : (
                      <>
                        {t.actions.loginButton.toUpperCase()} <ChevronRight className="h-6 w-6" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Auth Status Display */}
          {!loading && isLoggedIn && (
            <>
              <h3 className="mb-6 px-6 text-2xl font-semibold text-gray-800">{t.steps.login}</h3>
              <div className="mb-6 rounded-lg bg-gray-100 p-4">
                <div className="text-green-600">
                  ✓ {t.status.loggedIn} {user?.firstName} {user?.lastName} ({user?.email})
                </div>
              </div>
            </>
          )}

          {/* Registration Section */}
          <div className="rounded-lg p-6">
            <h3 className="mb-6 text-2xl font-semibold text-gray-800">{t.steps.register}</h3>
            <div className="space-y-4">
              {/* Row 1 */}
              <div className="grid gap-4 md:grid-cols-4">
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">{`${t.form.firstName}${t.form.required}`}</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.firstName}
                    required
                  />
                </div>
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">{`${t.form.lastName}${t.form.required}`}</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.lastName}
                    required
                  />
                </div>
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">{`${t.form.email}${t.form.required}`}</label>
                  <input
                    type="email"
                    name="emailReg"
                    value={formData.emailReg}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.email}
                    required
                  />
                </div>
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {`${t.form.phone}${t.form.required}`}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.phone}
                    required
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="grid gap-4 md:grid-cols-4">
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {`${t.form.street}${t.form.required}`}
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.street}
                    required
                  />
                </div>
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">{`${t.form.plz}${t.form.required}`}</label>
                  <input
                    type="text"
                    name="plz"
                    value={formData.plz}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.plz}
                    required
                  />
                </div>
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">{`${t.form.city}${t.form.required}`}</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    placeholder={t.form.city}
                    required
                  />
                </div>
              </div>

              {/* Row 5 */}
              <div className="grid gap-4 md:grid-cols-4">
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4 md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">{`${t.form.country}${t.form.required}`}</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder={t.form.selectCountry}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    required
                  />
                </div>
                <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {`${t.form.birthday}${t.form.required}`}
                  </label>
                  <input
                    type="date"
                    name="birthday"
                    value={formData.birthday}
                    onChange={handleInputChange}
                    className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                    required
                  />
                </div>
                {/* Only show password field if not logged in */}
                {!isLoggedIn && (
                  <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {`${t.form.setPassword}${t.form.required}`}
                    </label>
                    <input
                      type="password"
                      name="passwordReg"
                      value={formData.passwordReg}
                      onChange={handleInputChange}
                      className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                      placeholder={t.form.password}
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Family Camp Children Section */}
          {isFamilyCampFlag && (
            <div className="rounded-lg p-6">
              <h3 className="mb-4 text-2xl font-semibold text-gray-800">Kinderinformationen</h3>
              <p className="mb-6 text-gray-600">
                Bitte geben Sie die Informationen für jedes Kind an, das Sie zum Camp mitbringen
                möchten.
              </p>

              <div className="space-y-6">
                {formData.children.map((child, index) => (
                  <div key={index} className="">
                    <div className="mb-4 flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-800">
                        {t.sections.children.child} {index + 1}
                      </h4>
                      {formData.children.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChild(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          {t.sections.children.remove}
                        </button>
                      )}
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.sections.children.name}
                        </label>
                        <input
                          type="text"
                          value={child.name}
                          onChange={(e) => updateChild(index, 'name', e.target.value)}
                          className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                          placeholder="z.B. Maria"
                        />
                      </div>

                      <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t.sections.children.birthday}
                        </label>
                        <input
                          type="date"
                          value={child.birthday}
                          onChange={(e) => updateChild(index, 'birthday', e.target.value)}
                          className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
                        />
                      </div>
                    </div>

                    <div className="my-6 grid gap-4 md:grid-cols-2">
                      {showEmergencyEquipment && (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`needsEquipment-${index}`}
                            checked={child.needsEquipment}
                            onChange={(e) => updateChild(index, 'needsEquipment', e.target.checked)}
                            className="mr-2 h-4 w-4 text-accent focus:ring-accent"
                          />
                          <label htmlFor={`needsEquipment-${index}`} className="text-gray-700">
                            {t.sections.children.needsEquipment}
                          </label>
                        </div>
                      )}

                      {showLiftTicketOption && (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`needsLiftTicket-${index}`}
                            checked={child.needsLiftTicket}
                            onChange={(e) =>
                              updateChild(index, 'needsLiftTicket', e.target.checked)
                            }
                            className="mr-2 h-4 w-4 text-accent focus:ring-accent"
                          />
                          <label htmlFor={`needsLiftTicket-${index}`} className=" text-gray-700">
                            {t.sections.children.needsLiftTicket}
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addChild}
                  className="flex cursor-pointer items-center gap-2 text-lg font-bold uppercase text-black"
                >
                  + {t.sections.children.child} hinzufügen
                </button>
              </div>
            </div>
          )}

          {/* Alternative Date Section */}
          <div className="rounded-lg p-6">
            <h3 className="mb-4 text-2xl font-semibold text-gray-800">
              {t.sections.alternativeDate.title}
            </h3>
            <div className="">
              <div>
                <p className="mb-4 text-gray-600">{t.sections.alternativeDate.description}</p>
              </div>
              <div className="grid items-center gap-8 md:grid-cols-2">
                <select
                  name="alternativeCamp"
                  value={formData.alternativeCamp}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-gray-700 bg-white px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-accent"
                  disabled={loadingAlternatives}
                >
                  <option value="">
                    {loadingAlternatives
                      ? t.sections.alternativeDate.loadingAlternatives
                      : t.sections.alternativeDate.selectPlaceholder}
                  </option>
                  {alternativeCamps.map((camp) => (
                    <option key={camp.id} value={camp.id}>
                      {camp.title} - {new Date(camp.date).toLocaleDateString('de-DE')}
                    </option>
                  ))}
                  {!loadingAlternatives && alternativeCamps.length === 0 && (
                    <option value="" disabled>
                      {t.sections.alternativeDate.noAlternatives}
                    </option>
                  )}
                </select>

                {/* Dynamic link based on selected camp */}
                {formData.alternativeCamp ? (
                  <a
                    href={`/camps/${getSelectedAlternativeCamp()?.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black underline transition-colors hover:text-blue-600"
                  >
                    {t.sections.alternativeDate.viewDetails}
                  </a>
                ) : (
                  <span className="cursor-not-allowed text-gray-400">
                    {t.sections.alternativeDate.selectFirst}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg p-6">
            <div className="space-y-4">
              {showEmergencyEquipment && (
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="needsEquipment"
                    checked={formData.needsEquipment}
                    onChange={handleInputChange}
                    className="mr-3 mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="">{t.sections.equipment.emergencyEquipment}</span>
                </label>
              )}
              {showLiftTicketOption && (
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="needsLiftTicket"
                    checked={formData.needsLiftTicket}
                    onChange={handleInputChange}
                    className="mr-3 mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{t.sections.equipment.liftTicket}</span>
                </label>
              )}
              {showMultiPitchInterest && (
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="interestMultiPitch"
                    checked={formData.interestMultiPitch}
                    onChange={handleInputChange}
                    className="mr-3 mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{t.sections.equipment.multiPitchInterest}</span>
                </label>
              )}
            </div>
          </div>

          {/* Consent Section */}
          <div className="rounded-lg p-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-800">{t.sections.consent.title}</h3>
            <div className="space-y-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="photoConsent"
                  checked={formData.photoConsent}
                  onChange={handleInputChange}
                  className="mr-3 mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  required
                />
                <span
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{ __html: t.sections.consent.photoConsent }}
                />
              </label>
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="liabilityConsent"
                  checked={formData.liabilityConsent}
                  onChange={handleInputChange}
                  className="mr-3 mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  required
                />
                <span
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{ __html: t.sections.consent.liabilityConsent }}
                />
              </label>

              {/* Additional information text */}
              <div className="ml-8 mt-2 text-sm text-gray-600">
                <p>{t.sections.consent.additionalInfo}</p>
                <p className="mt-1">{t.sections.consent.liabilityConfirmation}</p>
              </div>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="rounded-lg p-6">
            <h3 className="mb-4 text-2xl font-semibold text-gray-800">
              {t.sections.newsletter.title}
            </h3>
            <p className="mb-4 text-gray-600">{t.sections.newsletter.description}</p>
            <label className="flex items-start">
              <input
                type="checkbox"
                name="newsletter"
                checked={formData.newsletter}
                onChange={handleInputChange}
                className="mr-3 mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">{t.sections.newsletter.consent}</span>
            </label>
          </div>

          {/* Submit Button */}
          <div className="text-left">
            <button
              type="submit"
              className="cursor-pointer border border-black px-6 py-4 text-lg font-bold uppercase text-black transition-colors hover:bg-accent/80"
            >
              {t.actions.submitButton}
            </button>
            <SubmitError />
          </div>
        </form>
      </div>
    </motion.div>
  )
}
